import { useState, useEffect, useCallback } from 'react';
import VoicePicker from '../chapters/VoicePicker';
import {
  getMySettings,
  patchMySettings,
  EmptyPatchError,
  VoiceValidationError,
} from '../../utils/apiUserSettings';
import * as voiceCatalogue from '../../utils/voiceCatalogue';

/**
 * SettingsForm — renders the per-user automation defaults
 * (automatic_processing, automatic_blitz, default_voice) backed by
 * GET / PATCH /me/settings (spec 010 US3).
 *
 * Sends ONLY changed fields on save (spec FR-008 / SC-005). Treats
 * `default_voice = ''` in the picker as the explicit "clear my override"
 * intent and serialises it to `null` in the PATCH body.
 *
 * The PhaseTriggerRequest `force` field is intentionally NOT surfaced here
 * — it remains available only to direct API callers (see spec 010 FR-006 /
 * data-model.md "API-only fields"). `tts_model` was removed from the
 * request schema in v0.10.0-rc1.
 *
 * 422 responses (VoiceValidationError) refresh the voice catalogue from the
 * server echo (preserving cached tts_model + system_default_voice) and
 * clear only the rejected `default_voice` from the draft — other unsaved
 * edits are preserved (SC-006).
 */
function SettingsForm() {
  const [serverState, setServerState] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getMySettings();
      setServerState(data);
      setDraft({
        automatic_processing:
          data && typeof data.automatic_processing === 'boolean'
            ? data.automatic_processing
            : true,
        automatic_blitz: !!(data && data.automatic_blitz),
        default_voice:
          data && typeof data.default_voice === 'string'
            ? data.default_voice
            : '',
      });
    } catch (e) {
      setError('Could not load settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const buildPartial = () => {
    if (!serverState || !draft) return {};
    const partial = {};
    if (draft.automatic_processing !== !!serverState.automatic_processing) {partial.automatic_processing = draft.automatic_processing;}
    if (draft.automatic_blitz !== !!serverState.automatic_blitz) {partial.automatic_blitz = draft.automatic_blitz;}
    const serverVoice =
      serverState.default_voice == null
        ? ''
        : String(serverState.default_voice);
    if (draft.default_voice !== serverVoice) {
      partial.default_voice =
        draft.default_voice === '' ? null : draft.default_voice;
    }
    return partial;
  };

  const handleSave = async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (saving || !draft) return;
    setError(null);
    setSuccess(null);
    const partial = buildPartial();
    if (Object.keys(partial).length === 0) {
      setSuccess('No changes to save.');
      return;
    }
    setSaving(true);
    try {
      const updated = await patchMySettings(partial);
      if (updated) setServerState(updated);
      setSuccess('Saved.');
    } catch (err) {
      if (err instanceof VoiceValidationError) {
        try {
          await voiceCatalogue.refreshFromErrorBody(err.body);
        } catch (_) {
          /* non-fatal */
        }
        setDraft((d) => (d ? { ...d, default_voice: '' } : d));
        setError(err.message);
      } else if (err instanceof EmptyPatchError) {
        setSuccess('No changes to save.');
      } else {
        setError('Could not save. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading || !draft) {
    return (
      <p className="text-sm text-gray-500" data-testid="settings-form-loading">
        Loading settings…
      </p>
    );
  }

  return (
    <form
      onSubmit={handleSave}
      data-testid="settings-form"
      className="space-y-4"
    >
      <label className="flex items-center gap-2 text-sm text-gray-200">
        <input
          type="checkbox"
          checked={draft.automatic_processing}
          onChange={(e) =>
            setDraft({ ...draft, automatic_processing: e.target.checked })
          }
          disabled={saving}
        />
        Automatically generate chapter audio after upload
      </label>
      <label className="flex items-center gap-2 text-sm text-gray-200">
        <input
          type="checkbox"
          checked={draft.automatic_blitz}
          onChange={(e) =>
            setDraft({ ...draft, automatic_blitz: e.target.checked })
          }
          disabled={saving}
        />
        Automatically generate key points after chapter audio (Blitz)
      </label>
      <div>
        <label
          htmlFor="settings-default-voice"
          className="block text-sm text-gray-200 mb-1"
        >
          Default voice
        </label>
        <VoicePicker
          id="settings-default-voice"
          value={draft.default_voice}
          onChange={(v) => setDraft({ ...draft, default_voice: v })}
          disabled={saving}
        />
        <p className="mt-1 text-xs text-gray-400">
          Used for audio generated from now on. Audio that already exists keeps
          the voice it was generated with.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={saving}
          className="px-3 py-1 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save settings'}
        </button>
        {success && (
          <span role="status" className="text-xs text-green-400">
            {success}
          </span>
        )}
        {error && (
          <span role="alert" className="text-xs text-red-400">
            {error}
          </span>
        )}
      </div>
    </form>
  );
}

export default SettingsForm;
